import { useEffect, useRef } from 'react';
import {
  calculateWedgeConfiguration,
  createDiamondAroundSegment,
  calculateProductLengths,
  createWedgeLabel,
  calculateRotationFromSegment,
} from './utils/wedge-utils';
import {
  AbstractSheetFilledShape,
  SheetFillStyleSet,
} from './abstract/AbstractSheetFilledShape';
import { ActiveSheetData } from 'src/components/viewers/SheetsViewerV2/hooks/useActiveSheet';
import { SheetShapeStyleType } from 'src/components/viewers/SheetsViewerV2/shapes/abstract/AbstractSheetShape';
import {
  SheetShapeDeepFragment,
  GetSparkelPropertiesForProjectQuery,
  SheetScaleType,
} from 'src/gql/graphql';
import { LineSegment2 } from 'src/domain/geometry/geometric-types';
import { toPoints2 } from 'src/domain/geometry/algorithms/util/type-mapping';
import {
  KileTypeSKU,
  Diamond,
} from 'src/components/orderContractor/pdf-shapes/protan/wedge-types';
import { getUnitScaleFromCalibration } from 'src/domain/sheet-calibration';
import Edit2D = Autodesk.Extensions.Edit2D;
import GuiViewer3D = Autodesk.Viewing.GuiViewer3D;
import Viewer3D = Autodesk.Viewing.Viewer3D;

// The actual shape class that handles rendering
class WedgeShapeClass extends AbstractSheetFilledShape {
  private diamonds: Diamond[] = [];
  private kileTypes: KileTypeSKU[] = [];
  private segmentMidpoints: [number, number][] = [];
  private propertyData?: GetSparkelPropertiesForProjectQuery;
  private calibration?: SheetScaleType;
  private triangleKeyMap?: Map<number, string>;

  constructor(
    viewer: Viewer3D | GuiViewer3D,
    edit2d: Edit2D,
    activeSheetData: ActiveSheetData,
    shapeModel?: SheetShapeDeepFragment,
    line?: THREE.Vector3[],
    styleSet?: SheetFillStyleSet,
    renderedSheetShapes?: Record<string, SheetShapeDeepFragment[]>,
    unitScale?: number,
    propertyData?: GetSparkelPropertiesForProjectQuery,
    calibration?: SheetScaleType
  ) {
    super(viewer, edit2d, activeSheetData, shapeModel);

    this.propertyData = propertyData;
    this.calibration = calibration;

    const convertedLine = this.getConvertedLine(shapeModel, line);
    this.validateInputs(shapeModel, line, convertedLine);

    // For diamond calculations, use raw PDF coordinates from shapeModel
    const pdfSegments: LineSegment2[] = [];
    if (shapeModel?.sheetShapeLine?.points) {
      for (let i = 0; i < shapeModel.sheetShapeLine.points.length - 1; i++) {
        pdfSegments.push([
          [
            shapeModel.sheetShapeLine.points[i].x,
            shapeModel.sheetShapeLine.points[i].y,
          ],
          [
            shapeModel.sheetShapeLine.points[i + 1].x,
            shapeModel.sheetShapeLine.points[i + 1].y,
          ],
        ]);
      }
    } else if (line) {
      // If no shapeModel, convert line to PDF-like coordinates (this is a fallback)
      for (let i = 0; i < line.length - 1; i++) {
        pdfSegments.push([
          [line[i].x, line[i].y],
          [line[i + 1].x, line[i + 1].y],
        ]);
      }
    }

    // Compute diamonds, kileTypes, and segmentMidpoints using PDF coordinates
    const { diamonds, kileTypes, segmentMidpoints } =
      calculateWedgeConfiguration(
        pdfSegments
          .map((segment) => segment[0])
          .concat([pdfSegments[pdfSegments.length - 1][1]]),
        renderedSheetShapes || {},
        unitScale || null
      );
    this.diamonds = diamonds;
    this.kileTypes = kileTypes;
    this.segmentMidpoints = segmentMidpoints;

    // Create shapes based on diamond configuration using converted coordinates for rendering
    this.createDiamondShapes(convertedLine || []);

    this.styleSet = styleSet || null;
    this.setStyle(SheetShapeStyleType.default);
  }

  private getConvertedLine(
    shapeModel?: SheetShapeDeepFragment,
    line?: THREE.Vector3[]
  ) {
    if (shapeModel && shapeModel.sheetShapeLine) {
      return shapeModel.sheetShapeLine.points.map((point) =>
        super.convertPointToRender(point)
      );
    }
    return line;
  }

  private validateInputs(
    shapeModel?: SheetShapeDeepFragment,
    line?: THREE.Vector3[],
    convertedLine?: { x: number; y: number }[]
  ) {
    if (shapeModel && !shapeModel.sheetShapeLine) {
      throw new Error('shapeModel.sheetShapeLine must be defined');
    } else if (!shapeModel && !Array.isArray(line)) {
      throw new Error('One shapeModel or point must be passed');
    } else if (!convertedLine) {
      throw new Error('Cannot define point');
    }
  }

  private createDiamondShapes(convertedLine: { x: number; y: number }[]) {
    this.shapes = [];
    const triangleLabelPoints: {
      x: number;
      y: number;
      segmentIndex: number;
    }[] = [];

    // Create diamond shapes based on the diamond configuration
    this.diamonds.forEach((diamond, index) => {
      if (index < convertedLine.length - 1) {
        const start = convertedLine[index];
        const end = convertedLine[index + 1];

        // Create segment in the format expected by createDiamondAroundSegment
        const segment: LineSegment2 = [
          [start.x, start.y],
          [end.x, end.y],
        ];

        // Determine diamond placement based on whether it's single-sided
        const isSingleSided =
          (!diamond.triangle1 && !diamond.triangle3) ||
          (!diamond.triangle2 && !diamond.triangle4);
        const diamondPlacement = isSingleSided ? 'ONESIDE' : 'CENTER';

        // Create triangle points using the same logic as DiamondLabel
        const trianglePoints = createDiamondAroundSegment(
          segment,
          diamondPlacement
        );

        // Create polygon shapes for each triangle that should be visible
        const diamondTriangles = [
          {
            key: 'triangle1',
            points: trianglePoints[0],
            visible: diamond.triangle1,
          },
          {
            key: 'triangle2',
            points: trianglePoints[1],
            visible: diamond.triangle2,
          },
          {
            key: 'triangle3',
            points: trianglePoints[2],
            visible: diamond.triangle3,
          },
          {
            key: 'triangle4',
            points: trianglePoints[3],
            visible: diamond.triangle4,
          },
        ];

        // Create polygon shapes for visible triangles and collect label points
        diamondTriangles.forEach(({ key, points, visible }) => {
          if (visible) {
            // Convert points to THREE.Vector3 for Autodesk shapes
            const vectorPoints = points.map(
              (point) => new THREE.Vector3(point[0], point[1], 0)
            );

            // Create polygon shape
            this.shapes.push(new Autodesk.Edit2D.Polygon(vectorPoints));

            // Calculate the center of this triangle for label positioning
            const centerX =
              points.reduce((sum, point) => sum + point[0], 0) / points.length;
            const centerY =
              points.reduce((sum, point) => sum + point[1], 0) / points.length;
            triangleLabelPoints.push({
              x: centerX,
              y: centerY,
              segmentIndex: index,
            });

            // Store triangle key mapping separately
            if (!this.triangleKeyMap) {
              this.triangleKeyMap = new Map();
            }
            this.triangleKeyMap.set(triangleLabelPoints.length - 1, key);
          }
        });
      }
    });

    // Set text label points to triangle centers
    this.textLabelPoints = triangleLabelPoints;
  }

  public getDiamonds() {
    return this.diamonds;
  }

  public getKileTypes() {
    return this.kileTypes;
  }

  public getSegmentMidpoints() {
    return this.segmentMidpoints;
  }

  public hitTest(x: number, y: number) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return this.shapes.some((shape) => shape.hitTest(x, y, 0.1));
  }

  // Override getLabels to return wedge labels with segment-based rotation
  public getLabels(options?: { fontSize?: number }): any {
    if (
      !this.apiModel?.sheetShapeLine ||
      !this.propertyData ||
      !this.calibration
    ) {
      const baseLabels = super.getLabels(options);
      return baseLabels.map((label) => ({
        ...label,
        fontSize: options?.fontSize || 12,
      }));
    }

    // Get the wedge points and create segments
    const wedgePoints = toPoints2(this.apiModel.sheetShapeLine.points);
    const segments: LineSegment2[] = [];
    for (let i = 0; i < wedgePoints.length - 1; i++) {
      segments.push([wedgePoints[i], wedgePoints[i + 1]]);
    }

    // Calculate product length for each segment based on its diamond configuration
    const unitScale = this.getUnitScaleFromCalibration();
    const segmentProductLengths = calculateProductLengths(
      segments,
      this.diamonds,
      unitScale
    );

    // Check if we have any valid product lengths
    const hasValidProducts = segmentProductLengths.some((length) => length > 0);
    if (!hasValidProducts) {
      const baseLabels = super.getLabels(options);
      return baseLabels.map((label) => ({
        ...label,
        fontSize: options?.fontSize || 12,
      }));
    }

    // Use the triangle centers we already calculated in createDiamondShapes
    const labels: {
      point: THREE.Vector3;
      label: string;
      rotation?: number;
      fontSize: number;
    }[] = [];

    // Group triangle centers by segment and side (left/right) to consolidate labels
    const segmentGroups: Record<
      number,
      {
        left: { x: number; y: number; segmentIndex?: number }[];
        right: { x: number; y: number; segmentIndex?: number }[];
      }
    > = {};

    // Initialize groups for each segment
    segments.forEach((_, segmentIndex) => {
      segmentGroups[segmentIndex] = { left: [], right: [] };
    });

    // Group triangle centers by their side position relative to the segment
    this.textLabelPoints.forEach((point, pointIndex) => {
      const segmentIndex = point.segmentIndex;

      // Skip if segmentIndex is undefined (shouldn't happen in WedgeShape, but for type safety)
      if (segmentIndex === undefined || segmentIndex >= segments.length) {
        return;
      }

      const segment = segments[segmentIndex];
      const segmentStart = segment[0];
      const segmentEnd = segment[1];

      // Use the triangle key to determine which side it's on
      // triangle1 and triangle3 are on the left side (triangle1=top right, triangle3=top left)
      // triangle2 and triangle4 are on the right side (triangle2=bottom right, triangle4=bottom left)
      const triangleKey = this.triangleKeyMap?.get(pointIndex);

      if (triangleKey === 'triangle1' || triangleKey === 'triangle3') {
        segmentGroups[segmentIndex].left.push(point);
      } else if (triangleKey === 'triangle2' || triangleKey === 'triangle4') {
        segmentGroups[segmentIndex].right.push(point);
      } else {
        // Fallback: put in left group if triangleKey is undefined
        segmentGroups[segmentIndex].left.push(point);
      }
    });

    // Create consolidated labels for each segment
    Object.entries(segmentGroups).forEach(([segmentIndexStr, group]) => {
      const segmentIndex = parseInt(segmentIndexStr);
      const segment = segments[segmentIndex];
      const segmentProductLength = segmentProductLengths[segmentIndex] || 0;
      const segmentWedgeLabel =
        segmentProductLength > 0 ? createWedgeLabel(segmentProductLength) : '';

      if (segmentWedgeLabel) {
        // Calculate rotation angle from segment direction, accounting for PDF rotation
        const pdfRotation = this.activeSheetData.viewport.rotation || 0;
        const rotation = calculateRotationFromSegment(segment, pdfRotation);

        // Create label for left side if there are triangles there
        if (group.left.length > 0) {
          // Use the average position of all left triangles
          const avgLeftX =
            group.left.reduce(
              (
                sum: number,
                p: { x: number; y: number; segmentIndex?: number }
              ) => sum + p.x,
              0
            ) / group.left.length;
          const avgLeftY =
            group.left.reduce(
              (
                sum: number,
                p: { x: number; y: number; segmentIndex?: number }
              ) => sum + p.y,
              0
            ) / group.left.length;
          labels.push({
            point: this.viewer.worldToClient(
              new THREE.Vector3(avgLeftX, avgLeftY, 0)
            ),
            label: segmentWedgeLabel,
            rotation: rotation,
            fontSize: options?.fontSize || 12, // Default smaller font size for wedge labels
          });
        }

        // Create label for right side if there are triangles there
        if (group.right.length > 0) {
          // Use the average position of all right triangles
          const avgRightX =
            group.right.reduce(
              (
                sum: number,
                p: { x: number; y: number; segmentIndex?: number }
              ) => sum + p.x,
              0
            ) / group.right.length;
          const avgRightY =
            group.right.reduce(
              (
                sum: number,
                p: { x: number; y: number; segmentIndex?: number }
              ) => sum + p.y,
              0
            ) / group.right.length;
          labels.push({
            point: this.viewer.worldToClient(
              new THREE.Vector3(avgRightX, avgRightY, 0)
            ),
            label: segmentWedgeLabel,
            rotation: rotation,
            fontSize: options?.fontSize || 12, // Default smaller font size for wedge labels
          });
        }
      }
    });

    return labels.map((label) => ({
      point: label.point,
      label: label.label,
      rotation: label.rotation,
      fontSize: label.fontSize,
    }));
  }

  // Helper to get unit scale from calibration
  private getUnitScaleFromCalibration(): number {
    if (!this.calibration) return 1;
    return getUnitScaleFromCalibration(this.calibration);
  }
}

// React functional component that uses the hook
interface WedgeShapeProps {
  viewer: Viewer3D | GuiViewer3D;
  edit2d: Edit2D;
  activeSheetData: ActiveSheetData;
  shapeModel?: SheetShapeDeepFragment;
  line?: THREE.Vector3[];
  styleSet?: SheetFillStyleSet;
  renderedSheetShapes?: Record<string, SheetShapeDeepFragment[]>;
  unitScale?: number;
  propertyData?: GetSparkelPropertiesForProjectQuery;
  calibration?: SheetScaleType;
  onShapeCreated?: (shape: WedgeShapeClass) => void;
}

function WedgeShapeComponent({
  viewer,
  edit2d,
  activeSheetData,
  shapeModel,
  line,
  styleSet,
  renderedSheetShapes,
  unitScale,
  propertyData,
  calibration,
  onShapeCreated,
}: WedgeShapeProps) {
  const shapeRef = useRef<WedgeShapeClass | null>(null);

  // Create the shape class instance when the hook data changes
  useEffect(() => {
    if (!viewer || !edit2d || !activeSheetData) return;

    // Clean up previous shape
    if (shapeRef.current) {
      shapeRef.current.remove();
    }

    // Create new shape with hook data
    const shape = new WedgeShapeClass(
      viewer,
      edit2d,
      activeSheetData,
      shapeModel,
      line,
      styleSet,
      renderedSheetShapes,
      unitScale,
      propertyData,
      calibration
    );

    shapeRef.current = shape;
    shape.draw();

    // Notify parent component
    if (onShapeCreated) {
      onShapeCreated(shape);
    }

    // Cleanup on unmount
    return () => {
      if (shapeRef.current) {
        shapeRef.current.remove();
        shapeRef.current = null;
      }
    };
  }, [
    viewer,
    edit2d,
    activeSheetData,
    shapeModel,
    line,
    styleSet,
    renderedSheetShapes,
    unitScale,
    propertyData,
    calibration,
    onShapeCreated,
  ]);

  // Return null since this component doesn't render anything visible
  return null;
}

// Export the class as default for backward compatibility with useShapes
export default WedgeShapeClass;

// Export the React component as a named export
export { WedgeShapeComponent };
